<script lang="ts">
	import { home_data, profile_data, user_data, user_settings } from 'src/stores/account/user';
	import { app } from 'src/stores/session/native-bridge';
	import { scrollToPremiumSubs } from 'src/stores/ui/promotions';
	import { onDestroy, onMount, setContext, tick } from 'svelte';
	import { Confetti } from 'svelte-confetti';
	import {
		getCurrentUnitPremiumInfo,
		getVoucherInfo,
		isPaypalInfo,
		hasValidVouchersInfo,
		buildVouchersDatesList,
		addDaysToTimestamp,
		getTotalVouchersDaysLeft,
		clearPremiumInfoCache,
		clearVoucherInfoCache,
	} from '../helpers/paypal_info_helpers';
	import { convertDatePayment, dateFormat, fromMillisecondsToDays } from '../helpers/date_helper';
	import {
		SubscriptionStatus,
		UserPremiumStatus,
		type PaypalInfoResponse,
		type PaypalInfoReturnValue,
		type VoucherDate,
		type VoucherResponse,
	} from '../types/Subscription';
	import {
		Alert,
		AlertV2,
		Text,
		Container,
		Stack,
		Icon,
		Label,
		Button,
		ButtonV2,
		PremiumCard,
		ProgressBar,
		Modal,
		Loader,
		Collapse,
		toast,
		Wizard,
		WizardItem,
		WizardContainer,
		WizardContent,
		LineContainer,
		Popover,
		CardGrid,
	} from '@Shelly/shelly-design-system';

	import InvoiceDetailsModal from '../components/Settings/Premium/Billing/InvoiceDetailsModal.svelte';
	import { unsubscribe, subscribe, getUserInfo } from '../helpers/subscription_helpers';
	import { format } from 'svelte-i18n';
	import InvoiceDetails from 'src/components/Settings/Premium/Billing/InvoiceDetails.svelte';
	import InvoiceHistory from 'src/components/Settings/Premium/Billing/InvoiceHistory.svelte';
	// import ForgottenLightsReminder from 'src/components/Settings/AccountSettings/ForgottenLightsReminder.svelte';
	import WeatherLocation from 'src/components/Settings/Premium/WeatherLocation.svelte';
	import SuccessfullySubscribed from 'src/components/Settings/Premium/SuccessfullySubscribed.svelte';
	import { get_user_settings, paypal, paypalTest } from 'src/services/http';
	import { writable } from 'svelte/store';
	import { querystring, location, push } from 'svelte-spa-router';
	import ValidateEmailPromptModal from 'src/components/ValidateEmailPromptModal.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import PremiumBenefitsList from 'src/components/Settings/Premium/PremiumBenefitsList.svelte';
	import AcceptTermsModal from 'src/components/Settings/Premium/AcceptTermsModal.svelte';
	import SetUpWeatherLocation from 'src/components/Weather/SetUpWeatherLocation.svelte';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import {
		setWizardSteps,
		updateWizardStepOnPrevNext,
		updateWizardStepOnWizardItem,
	} from 'src/components/Lists/list-helpers';
	import type {
		RegWeatherServiceResponse,
		WeatherStationSaveData,
	} from 'src/types/requests/services/weather';
	import { addWeatherStation, getWeatherStationData } from 'src/helpers/geolocalion_helper';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import PremiumBenefits from 'src/components/Promotions/PremiumBenefits.svelte';
	import { save_home } from 'src/helpers/home_helpers';
	import { save_profile } from 'src/helpers/profile_helpers';
	import { PROMOTIONS } from 'src/constants';

	import { isDevServerUser } from 'src/helpers/user_server_conditions';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import CancelSubscription from 'src/components/Settings/Premium/Billing/CancelSubscription.svelte';
	import VoucherActivatePremiumModal from 'src/components/Vouchers/VoucherCheckPremiumModal.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import ExpiredVouchers from 'src/components/Settings/Premium/Billing/ExpiredVouchers.svelte';
	import CameraCloudStorageSection from 'src/components/Settings/CameraCloudStorage/CameraCloudStorageSection.svelte';
	import CameraCloudStoragePlanInfoCard from 'src/components/Settings/CameraCloudStorage/CameraCloudStoragePlanInfoCard.svelte';
	import ManageCameraCloudStoragePanel from 'src/components/Settings/CameraCloudStorage/ManageCameraCloudStoragePanel.svelte';
	import { camera_cloud_storage } from 'src/stores/features/cameras/cloud-storage';
	import {
		buildPreviewActivePlan,
		clearDevPreview,
		DEV_PREVIEW_MIN_SLOTS,
		dev_modal_previews,
	} from 'src/stores/dev_modal_previews';
	import {
		accountHasUnassignedAssignableCamera,
		CAMERA_CLOUD_STORAGE_PLANS_SECTION_ID,
		CAMERA_CLOUD_STORAGE_SCROLL_PARAM,
		canManageCameraCloudStorageSlots,
		hydrateFromUserSettings,
	} from 'src/helpers/camera_cloud_storage_helpers';
	import { scrollElementIntoView } from 'src/helpers/scroll_position_helpers';
	import { CameraCloudStorageSubscriptionStatus } from 'src/types/CameraCloudStorage';

	const yearlySubValue = 35.99;
	const monthlySubValue = 3.99;
	const freePeriod = 3;

	const scrollToPlans = () => scrollElementIntoView(CAMERA_CLOUD_STORAGE_PLANS_SECTION_ID);

	const hasCameraPlansScrollFlag = (rawQuery: string | undefined): boolean => {
		const normalisedQuery = (rawQuery ?? '').replace(/\?/g, '&').replace(/^&+/, '');
		const params = normalisedQuery ? new URLSearchParams(normalisedQuery) : null;
		return params?.get(CAMERA_CLOUD_STORAGE_SCROLL_PARAM) === '1';
	};

	const scrollToCameraPlansFromQuery = async () => {
		if (!hasCameraPlansScrollFlag($querystring)) return;
		await scrollToPlans();
		push($location);
	};

	/**
	 * Show the Camera Cloud Storage cancel-success modal, then mark the
	 * plan Cancelled. Order matters: updating the store first would
	 * unmount the Cancel Subscription collapse and drop the event host.
	 */
	const handleCameraSubscriptionCancelled = () => {
		showCanceledCameraSubscriptionModal = true;
		camera_cloud_storage.updateActivePlanStatus(CameraCloudStorageSubscriptionStatus.Cancelled);
	};

	const handleRenewCameraCloudStorage = async () => {
		showCanceledCameraSubscriptionModal = false;
		await scrollToPlans();
	};
	const billing_components = [
		{
			title: 'billing_and_subscription',
			default: 'Billing and Subscription',
			components: [
				{
					title: 'invoice_details',
					default: 'Invoice Details',
					component: InvoiceDetails,
					id: 'invoiceDetails',
				},
				{
					title: 'invoice_history',
					default: 'Invoice History',
					component: InvoiceHistory,
					id: 'invoiceHistory',
				},
				// {
				// 	title: 'expired_vouchers',
				// 	default: 'Expired Vouchers',
				// 	component: ExpiredVouchers,
				// 	id: 'expiredVouchers',
				// },
				{
					title: 'button_subscription_cancel_subscription',
					default: 'Cancel Subscription',
					component: CancelSubscription,
					id: 'cancelSubscription',
				},
			],
		},
	];

	const settings_components = [
		{
			title: 'premium_features_settings_label',
			default: 'Premium Features Settings',
			components: [
				// {
				// 	title: 'forgotten_lights_reminder',
				// 	default: 'Forgotten lights reminder',
				// 	component: ForgottenLightsReminder,
				// 	id: 'forgottenLightsReminder'
				// },
				{
					title: 'weather_location',
					default: 'Weather Location',
					component: WeatherLocation,
					id: 'weatherLocation',
				},
			],
		},
	];

	let invoice_details_store = writable({});
	setContext('invoice_details', invoice_details_store);

	let weatherLocation = $user_settings?.weather_station?.data?.stationName;
	let waitForCacheTimeoutID: NodeJS.Timeout;
	let weatherStationSaveData: WeatherStationSaveData;
	let waitForSaveResponse = false;
	let windowClientWidth: number;

	let showConfetti = false;
	let showCancelSubscriptionModal = false;
	let showCanceledSubscriptionModal = false;
	let showCanceledCameraSubscriptionModal = false;
	let showSuccessfullySubscribedModal = false;
	let showInvoiceDetailsModal = false;
	let showAcceptTermsModal = false;
	let toggled = false;
	let currentType = 0;
	let isMonthlySubscribeLoading = false;
	let isYearlySubscribeLoading = false;
	let isEmailValidationModal = false;
	let premiumInfoKey = 0;

	let sharedHomeSubscriptionLocked = isActiveHomeShared();

	let includeCameraStorageStep = false;
	let manageStoragePanel;
	let manageStorageView: 'main' | 'add' | 'replace' = 'main';

	let devWizardPreview = false;

	$: devPreviewPlan = devWizardPreview
		? ($camera_cloud_storage.activePlan ?? buildPreviewActivePlan($dev_modal_previews.tier))
		: null;
	$: devPreviewSlotCount = devWizardPreview
		? Math.max(DEV_PREVIEW_MIN_SLOTS, devPreviewPlan?.tier ?? 0)
		: null;

	$: if ($dev_modal_previews.flow === 'welcome_premium') openDevWizardPreview();

	function openDevWizardPreview() {
		clearDevPreview();
		devWizardPreview = true;
		wizardItems = buildWelcomeWizardItems();
		activeWizardStep = wizardItems[0];
		showSuccessfullySubscribedModal = true;
	}

	function shouldIncludeCameraStorageStep(): boolean {
		return canManageCameraCloudStorageSlots() && accountHasUnassignedAssignableCamera();
	}

	function hasWeatherLocationAlready(): boolean {
		return !!(weatherLocation && weatherLocation.length > 0);
	}

	function buildWelcomeWizardItems() {
		includeCameraStorageStep = shouldIncludeCameraStorageStep();
		const steps = [capFirstChar($format('welcome_message', { default: 'Welcome' }))];
		if (includeCameraStorageStep) {
			steps.push(
				capFirstChar(
					$format('modal_title_manage_camera_cloud_storage', {
						default: 'Manage Camera Cloud Storage',
					}),
				),
			);
		}
		if (devWizardPreview || !hasWeatherLocationAlready()) {
			steps.push(
				capFirstChar(
					$format('set_up_weather_location', {
						default: 'Set Up Weather Location',
					}),
				),
			);
		}
		return setWizardSteps(steps);
	}

	let wizardItems = setWizardSteps([
		capFirstChar($format('welcome_message', { default: 'Welcome' })),
		capFirstChar(
			$format('set_up_weather_location', {
				default: 'Set Up Weather Location',
			}),
		),
	]);
	let activeWizardStep = wizardItems[0];
	let setHight100Class = false;

	let collapsesExpanded = {
		accAccess: false,
		authKey: false,
		notification: false,
		privacyPolicy: false,
		language: false,
		timeZone: false,
		subscription: false,
		changeEmail: false,
		changePass: false,
		deleteAcc: false,
	};

	enum SubscriptionError {
		AlreadySubscribed = 'you already have an active subscription',
		NotPremium = 'not premium',
		CannotFindSubscription = 'cannot find subscription, nothing to upgrade',
		NotMonthly = 'subscription is not monthly, nothing to upgrade',
		NotActive = 'subscription is not active, nothing to upgrade',
		CreationError = 'Error creating subscription',
		MissingRedirectHeader = "missing 'x-shelly-redirect' header",
		MissingInvoiceData = 'missing invoice data in database',
		NotSubscribed = 'not subscribed',
	}

	export function translateSubscriptionError(errorMessage: string): string {
		switch (errorMessage) {
			case SubscriptionError.AlreadySubscribed:
				return $format('toast_error_premium_already_subscribed', {
					default: 'You already have an active subscription.',
				});

			case SubscriptionError.NotPremium:
				return $format('toast_error_premium_not_premium.', {
					default: 'You are not a premium user.',
				});

			case SubscriptionError.CannotFindSubscription:
				return $format('toast_error_premium_nothing_to_upgrade', {
					default: 'Cannot find subscription, nothing to upgrade.',
				});

			case SubscriptionError.NotMonthly:
				return $format('toast_error_premium_not_monthly', {
					default: 'Subscription is not monthly, nothing to upgrade.',
				});

			case SubscriptionError.NotActive:
				return $format('toast_error_premium_not_active', {
					default: 'Subscription is not active, nothing to upgrade.',
				});

			case SubscriptionError.CreationError:
				return $format('toast_error_premium_generic', {
					default: 'Error creating subscription.',
				});

			case SubscriptionError.MissingRedirectHeader:
				return $format('toast_error_premium_missing_redirect_header', {
					default: 'Subscription is not active, nothing to upgrade.',
				});

			case SubscriptionError.MissingInvoiceData:
				return $format('toast_error_premium_missing_invoice_data', {
					default: 'Missing invoice data.',
				});

			case SubscriptionError.NotSubscribed:
				return $format('toast_error_premium_not_subscribed', {
					default: 'You are not subscribed.',
				});

			default:
				return $format('toast_error_premium_generic_fallback', {
					default: 'Something went wrong, please try again later.',
				});
		}
	}

	function getTranslatedErrorMessage(error: any): string {
		const errorMessage = error?.response?.data?.error;
		return translateSubscriptionError(errorMessage);
	}

	//#region Wizard functions
	const onClickWizardItem = (index: number) => {
		const result = updateWizardStepOnWizardItem(index, wizardItems, activeWizardStep);
		wizardItems = result.wizardItems;
		activeWizardStep = { ...result.activeWizardStep };
	};

	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		const result = updateWizardStepOnPrevNext(type, wizardItems, activeWizardStep);
		wizardItems = result.wizardItems;
		activeWizardStep = { ...result.activeWizardStep };
	};

	const onCloseWizard = () => {
		clearTimeout(waitForCacheTimeoutID);
		showSuccessfullySubscribedModal = false;
		devWizardPreview = false;
		clearDevPreview();
		clearData();
	};

	const goToNextWelcomeStep = () => {
		if (activeWizardStep.activeIndex >= wizardItems.length - 1) {
			onCloseWizard();
			return;
		}
		onClickPrevNextButton('next');
	};

	const saveCameraSlotsAndContinue = async () => {
		const ok = await manageStoragePanel?.saveAssignments();
		if (ok) goToNextWelcomeStep();
	};

	const clearData = () => {
		activeWizardStep = wizardItems[0];
		setHight100Class = false;
		manageStorageView = 'main';
	};
	//#endRegion Wizard functions

	const startConfetti = () => {
		setTimeout(() => {
			showConfetti = true;
		}, 800);
	};

	const getInvoiceDetails = async () => {
		let instance = paypal;

		if (isDevServerUser(['repo'])) {
			instance = paypalTest;
		}

		try {
			const response = await instance.get('/api/invoice_data');

			if (response && 'type' in response && 'country' in response) {
				invoice_details_store.set(response);

				return;
			}

			throw new Error('Invalid response format when fetching invoice details');
		} catch (error) {
			console.error(error.message);
			invoice_details_store.set({});
		}
	};

	/**
	 * Single in-flight PayPal-info / vouchers fetch shared by `onMount` and
	 * both `{#await}` blocks in the template. Without this, the three call
	 * sites would each kick off their own `get_premium_units` + `all_coupons`
	 * pair on a single page load.
	 *
	 * `refreshPaypalInfo` is the *passive* refresher used at module init and
	 * by anything that just wants the current data. It relies on the
	 * short-TTL dedup cache in `paypal_info_helpers.ts` so that the layout
	 * swap re-mount (MainLayout → MainLayoutNew via `MainLayoutManager`),
	 * `InvoiceDetails`'s own `onMount` fetch, and the `http.ts` bootstrap
	 * call all collapse into a single network request per refresh.
	 *
	 * `forceRefreshPaypalInfo` is the *active* refresher used after explicit
	 * state mutations (cancel subscription, voucher modal close, native app
	 * focus) where we must bypass the cache to surface fresh server state.
	 */
	let paypalInfoPromise: Promise<PaypalInfoReturnValue>;

	const refreshPaypalInfo = (): Promise<PaypalInfoReturnValue> => {
		paypalInfoPromise = awaitPaypalInfo();
		return paypalInfoPromise;
	};

	const forceRefreshPaypalInfo = (): Promise<PaypalInfoReturnValue> => {
		clearPremiumInfoCache();
		clearVoucherInfoCache();
		return refreshPaypalInfo();
	};

	const awaitPaypalInfo = async (): Promise<PaypalInfoReturnValue> => {
		let paypalInfoResponse = (await getCurrentUnitPremiumInfo()) as PaypalInfoResponse;
		let vouchersInfoResponse = (await getVoucherInfo()) as VoucherResponse[];

		const vouchersDates: VoucherDate[] = buildVouchersDatesList(vouchersInfoResponse);

		let whatIsHappening = {
			hasVouchers:
				hasValidVouchersInfo(vouchersInfoResponse) &&
				vouchersDates.filter((voucherDate) => voucherDate.status !== 'used').length > 0,
			hasPremium: isPaypalInfo(paypalInfoResponse),
		};

		if (vouchersDates.filter((voucherDate) => voucherDate.status === 'used').length > 0) {
			if (
				!billing_components[0].components.find((component) => component.id === 'expiredVouchers')
			) {
				billing_components[0].components.splice(2, 0, {
					title: 'expired_vouchers',
					default: 'Expired Vouchers',
					component: ExpiredVouchers as any,
					id: 'expiredVouchers',
				});
			}
		} else {
			if (
				billing_components[0].components.find((component) => component.id === 'expiredVouchers')
			) {
				billing_components[0].components.splice(2, 1);
			}
		}

		if (!isPaypalInfo(paypalInfoResponse)) {
			paypalInfoResponse = {
				created_time: '',
				free_trial: '',
				last_billing_time: '',
				next_billing_time: '',
				start_time: '',
				subscription_id: '',
				subscription_plan_type: '',
				subscription_status: SubscriptionStatus.Unknown,
			} satisfies PaypalInfoResponse;
		}
		if (!hasValidVouchersInfo(vouchersInfoResponse)) {
			vouchersInfoResponse = [
				{
					code: '',
					status: '',
					type: '',
					uid: 0,
					duration: 0,
					aat: '',
					iat: '',
				},
			];
		}

		const billingTotalDays = fromMillisecondsToDays(
			paypalInfoResponse.next_billing_time,
			paypalInfoResponse.last_billing_time,
		);

		const totalVouchersDaysLeft =
			vouchersDates.length > 0 ? getTotalVouchersDaysLeft(vouchersDates) : 0;

		let initialTotalVouchersDaysLeft = 0;

		if (vouchersDates.length > 0) {
			initialTotalVouchersDaysLeft = $home_data?.initialTotalVouchersDaysLeft;
			if ([undefined, null].includes(initialTotalVouchersDaysLeft)) {
				// first time vouchers appear → persist baseline
				initialTotalVouchersDaysLeft = totalVouchersDaysLeft;
				save_home('initialTotalVouchersDaysLeft', initialTotalVouchersDaysLeft);
			}

			// safety: if vouchers were fully consumed, reset baseline
			if (totalVouchersDaysLeft === 0) {
				save_home('initialTotalVouchersDaysLeft', 0);
			}
		}

		const today = new Date();
		today.setMinutes(today.getMinutes() - today.getTimezoneOffset());

		const rawBillingTimePassed = fromMillisecondsToDays(
			today.toISOString(),
			paypalInfoResponse.last_billing_time,
		);

		const voucherDaysConsumed = Math.max(0, initialTotalVouchersDaysLeft - totalVouchersDaysLeft);

		const billingTimePassed = Math.max(0, rawBillingTimePassed - voucherDaysConsumed);

		const billingProgressPercentage = (billingTimePassed * 100) / billingTotalDays;

		const subscriptionPlan = paypalInfoResponse?.subscription_plan_type;

		const subscriptionStatus = paypalInfoResponse?.subscription_status;

		const subscriptionText = subscriptionStatus === SubscriptionStatus.Active ? 'Active' : 'Renew';

		const nextBillingTime =
			vouchersDates.length > 0
				? dateFormat(
						addDaysToTimestamp(
							new Date(paypalInfoResponse.next_billing_time).getTime(),
							totalVouchersDaysLeft,
						),
						'Y-m-d',
					)
				: paypalInfoResponse.next_billing_time;

		return {
			paypalInfoResponse: {
				...paypalInfoResponse,
				free_trial:
					paypalInfoResponse?.is_currently_in_free_trial ?? paypalInfoResponse.free_trial,
			},
			billingTimePassed,
			billingTotalDays,
			billingProgressPercentage,
			subscriptionPlan,
			subscriptionStatus,
			subscriptionText,
			vouchersDates,
			whatIsHappening,
			vouchersInfoResponse,
			nextBillingTime,
		};
	};

	// Seed the shared promise so the first render of both `{#await}` blocks
	// (and `onMount`) reuses one in-flight fetch instead of issuing their own.
	refreshPaypalInfo();

	const handleSubscriptionChecks = (type) => {
		if (sharedHomeSubscriptionLocked) {
			return;
		}

		currentType = type;

		if (isEmailValidated === 1) {
			if (Object.keys($invoice_details_store).length === 0) {
				showInvoiceDetailsModal = true;
			} else {
				showAcceptTermsModal = true;
			}
		} else {
			isEmailValidationModal = true;
		}

		isMonthlySubscribeLoading = false;
		isYearlySubscribeLoading = false;
	};

	const filterType = (type) => {
		return {
			1: 'monthly',
			2: 'yearly',
			3: 'upgrade',
		}[type];
	};

	const onSubscribe = async (type) => {
		const isMonthly = type === 1;
		if (isMonthly) {
			isMonthlySubscribeLoading = true;
		} else {
			isYearlySubscribeLoading = true;
		}
		try {
			await subscribe(filterType(type));
		} catch (error) {
			const translatedMessage = getTranslatedErrorMessage(error);

			toast.open({
				type: 'danger',
				title: translatedMessage,
			});
		} finally {
			if (isMonthly) {
				isMonthlySubscribeLoading = false;
			} else {
				isYearlySubscribeLoading = false;
			}
		}
	};

	const handleCancelSubscription = async () => {
		showCancelSubscriptionModal = true;
		try {
			const res = await unsubscribe();
			if ('premium' in res && typeof res.premium !== 'undefined') {
				user_data.update(res);

				// update user settings
				await get_user_settings();

				// show canceled subscription modal (sorry to see you go)
				showCanceledSubscriptionModal = true;
				// hide cancel confirmation modal
				showCancelSubscriptionModal = false;

				forceRefreshPaypalInfo();
				toggled = !toggled;
			}
		} catch (error) {
			const translatedMessage = getTranslatedErrorMessage(error);

			toast.open({
				type: 'danger',
				title: translatedMessage,
			});
		}
	};

	const handleRenew = async (type) => {
		if (sharedHomeSubscriptionLocked || isMonthlySubscribeLoading) {
			return;
		}

		await handleSubscriptionChecks(type);
	};

	const saveStation = async () => {
		if (devWizardPreview) {
			onCloseWizard();
			return;
		}
		waitForSaveResponse = true;
		clearTimeout(waitForCacheTimeoutID);
		const res: RegWeatherServiceResponse = await addWeatherStation(weatherStationSaveData);
		if (res?.isok && !res?.data?.old) {
			waitForCacheTimeoutID = setTimeout(saveStation, 1500);
		} else {
			waitForSaveResponse = false;
			onSettingsResponse(res);
			if (res?.isok) onCloseWizard();
		}
	};

	const updateHomeData = () => {
		const dateNow = new Date();

		save_profile('premiumBlackFridayAlert', {
			seen: true,
			date: dateNow,
		});

		profile_data.set({
			...$profile_data,
			premiumBlackFridayAlert: {
				seen: true,
				date: dateNow,
			},
		});
	};

	let nextBillingPrice = null;
	let subscriptionsContainerRef: HTMLElement = null;
	let premiumSettingsReady = false;

	const planWithFreeTrial = new Set(['P-1T317074G4005084KM5DPWSI', 'P-5N0404803C6949805M5CG57Y']);

	let openVoucherModal: boolean = false;
	//  check if have QR voucher code or Voucher link from User settings
	let qrVoucherCode = new URLSearchParams($querystring).get('vcode');
	const voucherModal = new URLSearchParams($querystring).get('v');
	if (qrVoucherCode || voucherModal) {
		openVoucherModal = true;
	}

	onMount(async () => {
		try {
			const paypalInfo = await paypalInfoPromise;
			const { start_time, plan_id } = paypalInfo?.paypalInfoResponse || {};

			// Calculate start date and end of free trial
			let endOfFreeTrialDate = new Date(start_time);
			endOfFreeTrialDate.setMonth(endOfFreeTrialDate.getMonth() + 3);

			// Determine next billing price
			if (plan_id && planWithFreeTrial.has(plan_id)) {
				const isFreeTrialActive = endOfFreeTrialDate.getTime() >= Date.now();
				nextBillingPrice = isFreeTrialActive ? 24.99 : 35.99;
			} else {
				// If plan_id is missing or not in the free trial set
				nextBillingPrice = 35.99;
			}
		} catch (error) {
			console.error('Error while fetching PayPal info:', error);
		}

		if ($querystring === 'state=1') {
			try {
				// get user data from the server (without login)
				const res = await getUserInfo();

				// check if the premium status 1 is confirmed with the user data from the server
				if (res?.premium === 1) {
					user_data.update(res);
					await get_user_settings();
					hydrateFromUserSettings($user_settings);
					weatherLocation = $user_settings?.weather_station?.data?.stationName;
					wizardItems = buildWelcomeWizardItems();
					activeWizardStep = wizardItems[0];
					showSuccessfullySubscribedModal = true;

					// Retrieve weather station data after successfully subscribing to premium, if the user had already set up a weather station before.
					if ($user_settings?.weather_station?.data?.stationName) {
						getWeatherStationData($user_settings.weather_station.l, true);
					}
				} else {
					throw {
						response: {
							data: {
								error: 'UNKNOWN_CLIENT_ERROR', // intentionally want to throw the default error
							},
						},
					};
				}
			} catch (error) {
				const translatedMessage = getTranslatedErrorMessage(error);

				toast.open({
					type: 'danger',
					title: translatedMessage,
				});
			} finally {
				// clear querystring parameter state=1
				push($location);
			}
		}
		await getInvoiceDetails();

		await tick();
		if ($scrollToPremiumSubs) {
			subscriptionsContainerRef.scrollIntoView({ behavior: 'smooth' });
			$scrollToPremiumSubs = false;
		}
		await scrollToCameraPlansFromQuery();
		premiumSettingsReady = true;
	});

	onDestroy(() => {
		clearTimeout(waitForCacheTimeoutID);
	});

	if ($querystring === 'state=0') {
		toast.open({
			type: 'danger',
			title: $format('toast_premium_error', {
				default: 'Something went wrong',
			}),
		});
	}

	if ($app) {
		window.addEventListener('focus', () => {
			forceRefreshPaypalInfo();
			toggled = !toggled;
		});
	}

	$: isEmailValidated = $user_settings.mail_validated;
	$: isLastStepWizard = activeWizardStep.activeIndex === wizardItems.length - 1;
	$: isFirstStep = activeWizardStep.activeIndex === 0;
	$: isManageStorageStep = includeCameraStorageStep && activeWizardStep.activeIndex === 1;
	$: weatherStepIndex = includeCameraStorageStep ? 2 : 1;
	$: isWeatherStep = activeWizardStep.activeIndex === weatherStepIndex;
	$: hideWelcomeChrome =
		!devWizardPreview &&
		!!(weatherLocation && weatherLocation.length > 0) &&
		!includeCameraStorageStep;
	$: nextButton = isLastStepWizard ? $format('button_save') : $format('button_next');
	$: isMobile = windowClientWidth < SystemScreenBreakpoints.MOBILE_MAX;
	$: WelcomeWizardButton = $showNewLayout ? ButtonV2 : Button;
	$: welcomeWizardButtonClass = $showNewLayout
		? 'min-width-md-m width-md-auto width-sm-full text-nowrap'
		: 'width-md-m width-sm-full';
	$: if (premiumSettingsReady && hasCameraPlansScrollFlag($querystring)) {
		void scrollToCameraPlansFromQuery();
	}

	$: if (showSuccessfullySubscribedModal) {
		startConfetti();
	}
	$: isPremium = $user_data.premium;

	// Use to set correct size for the map, after the step with map is active
	$: if (isWeatherStep) {
		setTimeout(() => {
			setHight100Class = true;
			// Time before step transition duration end
		}, 400);
	} else {
		setHight100Class = false;
	}

	const IS_DURING_PROMOTION =
		new Date().getTime() > PROMOTIONS.BLACK_FRIDAY.START_DATE &&
		new Date().getTime() < PROMOTIONS.BLACK_FRIDAY.END_DATE;

	const ALL_BLACK_FRIDAY_PLANS = [
		'P-6E080058C6513084TM4NDQFY',
		'P-5N0404803C6949805M5CG57Y',
		'P-3M0352906R383414XM4PXSSI',
		'P-1T317074G4005084KM5DPWSI',
	];

	const determinePremiumCard = (paypalInfo) => {
		if (!paypalInfo) return null;

		const BLACK_FRIDAY_PLAN = ALL_BLACK_FRIDAY_PLANS.includes(
			paypalInfo.paypalInfoResponse?.plan_id,
		);
		if (!isPremium) {
			if (IS_DURING_PROMOTION) {
				// If Basic user during promotion
				return UserPremiumStatus.BASIC_FROM_BF_DURING;
			}
			return UserPremiumStatus.BASIC_DEFAULT;
		} else {
			if (BLACK_FRIDAY_PLAN && IS_DURING_PROMOTION) {
				// If Premium during promotion
				return UserPremiumStatus.PREMIUM_FROM_BF_DURING;
			} else if (BLACK_FRIDAY_PLAN && new Date().getTime() > PROMOTIONS.BLACK_FRIDAY.END_DATE) {
				// If Premium after promotion
				return UserPremiumStatus.PREMIUM_FROM_BF_AFTER;
			}
			return UserPremiumStatus.PREMIUM_DEFAULT;
		}
	};

	let voucherModalKey: number = 0;
	const closeVoucherModal = async () => {
		openVoucherModal = false;
		voucherModalKey += 1;
		premiumInfoKey += 1;
		forceRefreshPaypalInfo();
		qrVoucherCode = null;
		toggled = !toggled;
	};
</script>

{#if isEmailValidationModal}
	<ValidateEmailPromptModal
		bind:showModal={isEmailValidationModal}
		on:close={() => (isEmailValidationModal = false)}
	/>
{/if}

<svelte:window bind:innerWidth={windowClientWidth} />

{#key toggled}
	<LineContainer
		accentColor={isPremium ? '--gradient-premium' : '--gradient-primary-horizontal'}
		class="mb-s"
	>
		{#await paypalInfoPromise}
			<div class="d-flex align-items-center justify-content-center width-full">
				<Loader />
			</div>
		{:then paypalInfo}
			<div
				class={classMap({
					'd-flex flex-row width-full': true,
					'gap-l': !isMobile,
					'flex-direction-column gap-m': isMobile,
				})}
			>
				{#if openVoucherModal}
					{#key voucherModalKey}
						<VoucherActivatePremiumModal
							bind:openVoucherModal
							on:close={closeVoucherModal}
							on:noEmailVerified={() => {
								isEmailValidationModal = true;
							}}
							{qrVoucherCode}
							{paypalInfo}
						/>
					{/key}
				{/if}

				{#if isPremium}
					<div class="width-full">
						<div class="d-flex align-items-center gap-s">
							<Text class="icon-premium-color" styleType="h1" noWrap>
								{$format('premium', { default: 'Premium' })}
							</Text>
							<Icon size="s" icon="fas fa-gem icon-premium-color" />
						</div>
						{#if paypalInfo.whatIsHappening.hasVouchers}
							<Text
								styleType="body-small"
								class="text-uppercase text-color-muted text-bold mb-s mt-m"
								noWrap
							>
								{$format('label_premium_vouchers', {
									default: 'Premium Vouchers',
								})}
							</Text>
							<CardGrid sm={300} lg={350} xl={425} noPaddingBottom>
								{#each paypalInfo.vouchersDates.filter((voucherDate) => voucherDate.status !== 'used') as voucherDate}
									<Popover
										type={voucherDate.status === 'on_hold' ? 'hover' : 'click'}
										placement="top"
										hideCloseBtn
									>
										<div
											class={`width-full ${classMap({
												'lower-opacity': ['used', 'on_hold'].includes(
													voucherDate.status,
												),
											})}`}
										>
											<Container padding="0" type="modalContainer">
												<div class="mt-m mb-m ml-m mr-m">
													<div
														class="d-flex justify-content-between align-items-center"
													>
														<div class="d-flex align-items-center gap-s">
															<Icon
																size="s"
																icon="fas fa-ticket"
																class="icon-premium-color"
															/>
															<Text
																styleType="body-small"
																class="text-bold"
																>{voucherDate.code}</Text
															>
														</div>
														<div class="d-flex align-items-center gap-xs">
															{#if voucherDate.status === 'used'}
																<Text
																	class="text-bold"
																	styleType="body-small"
																	noWrap
																>
																	{$format('label_expired_voucher', {
																		default: 'Expired voucher',
																	})}
																</Text>
															{:else if voucherDate.status === 'on_hold'}
																<Text
																	class="text-bold"
																	styleType="body-small"
																	noWrap
																>
																	{$format('label_on_hold', {
																		default: 'On hold',
																	})}
																</Text>
																<Icon
																	size="s"
																	icon="fas fa-circle-pause"
																/>
															{:else}
																<Text
																	class="text-success text-bold"
																	styleType="body-small"
																	noWrap
																>
																	{capFirstChar(
																		$format('active', {
																			default: 'Active',
																		}),
																	)}
																</Text>
																<Icon
																	size="s"
																	icon="fas fa-circle-check text-success"
																/>
															{/if}
														</div>
													</div>
													<ProgressBar
														class="width-full mt-s mb-s"
														progress={Math.round(
															(((voucherDate.status === 'on_hold'
																? 0
																: voucherDate.duration) -
																voucherDate.daysPassed) /
																voucherDate.duration) *
																100,
														)}
													/>
													<div
														class="d-flex justify-content-between align-items-start gap-s"
													>
														<div class="d-flex align-items-center">
															<Text styleType="body-small" noWrap>
																{#if voucherDate.status === 'used'}
																	{$format('label_expired', {
																		default: 'Expired',
																	})}
																{:else}
																	{(voucherDate.status === 'on_hold'
																		? 0
																		: voucherDate.duration) -
																		voucherDate.daysPassed}/{voucherDate.duration}
																	{$format(
																		'text_premium_time_passed',
																		{
																			default: 'days',
																		},
																	)}
																{/if}
															</Text>
														</div>
														<div class="text-right flex-shrink-0">
															<Text
																styleType="body-small"
																class="text-uppercase text-color-muted text-bold"
																noWrap
															>
																{$format('heading_voucher_valid_until', {
																	default: 'Valid until',
																})}
															</Text>
															<Text styleType="body-small" noWrap>
																{convertDatePayment(voucherDate.end)}
															</Text>
														</div>
													</div>
												</div>
											</Container>
										</div>
										<Text slot="customContent" styleType="body-small">
											{$format('text_popover_voucher_on_hold', {
												default:
													'This voucher will be automatically activated after the current one expires. Valid until {endDate}.',
												values: {
													endDate: convertDatePayment(voucherDate.end),
												},
											})}
										</Text>
									</Popover>
								{/each}
							</CardGrid>
						{/if}
						{#if paypalInfo.whatIsHappening.hasPremium}
							<Text
								styleType="body-small"
								class="text-uppercase text-color-muted text-bold mb-s mt-m"
								noWrap
							>
								{$format('label_premium_plan', {
									default: 'Premium Plan',
								})}
							</Text>
							<CardGrid sm={300} lg={350} xl={425} noPaddingBottom>
								<Popover
									type={paypalInfo.subscriptionStatus === 'SUSPENDED' &&
									paypalInfo.whatIsHappening.hasVouchers &&
									paypalInfo.billingTotalDays - paypalInfo.billingTimePassed > 0
										? 'hover'
										: 'click'}
									placement="top"
									hideCloseBtn
								>
									<div
										class={`width-full ${classMap({
											'lower-opacity':
												paypalInfo.subscriptionStatus === 'SUSPENDED',
										})}`}
									>
										<Container padding="0" type="modalContainer">
											<div class="mt-m mb-m ml-m mr-m">
												<div
													class="d-flex justify-content-between align-items-center"
												>
													<div class="d-flex align-items-center gap-s">
														<Icon
															size="s"
															icon="fas fa-gem"
															class="icon-premium-color"
														/>
														<div>
															<Text
																styleType="body-small"
																class="text-bold"
																noWrap
															>
																{paypalInfo.paypalInfoResponse
																	?.free_trial === '1'
																	? `${$format(
																			'plan_information_free_trial',
																			{
																				default: 'Free Trial',
																			},
																		)} (${capFirstChar($format(paypalInfo.subscriptionPlan, { default: paypalInfo.subscriptionPlan }))})`
																	: $format('label_premium_plan', {
																			default: 'Premium Plan',
																		})}
															</Text>
															<Text styleType="body-small" noWrap>
																{paypalInfo.paypalInfoResponse
																	?.free_trial === '1'
																	? '€0'
																	: paypalInfo.subscriptionPlan ===
																		  'monthly'
																		? $format('label_price_month', {
																				default:
																					'€{price}/month',
																				values: {
																					price: monthlySubValue,
																				},
																			})
																		: $format('label_price_year', {
																				default: '€{price}/year',
																				values: {
																					price: yearlySubValue,
																				},
																			})}
															</Text>
														</div>
													</div>
													<div
														class="d-flex align-items-center gap-xs flex-shrink-0"
													>
														{#if paypalInfo.subscriptionStatus === 'ACTIVE'}
															<Text
																class="text-success text-bold"
																styleType="body-small"
																noWrap
															>
																{capFirstChar(
																	$format('active', {
																		default: 'Active',
																	}),
																)}
															</Text>
															<Icon
																size="s"
																icon="fas fa-circle-check text-success"
															/>
														{:else if paypalInfo.subscriptionStatus === 'SUSPENDED'}
															<Text
																class="text-bold"
																styleType="body-small"
																noWrap
															>
																{$format('label_paused', {
																	default: 'Paused',
																})}
															</Text>
															<Icon size="s" icon="fas fa-circle-pause" />
														{:else if paypalInfo.subscriptionStatus === 'CANCELLED'}
															<Text
																class="text-danger text-bold"
																styleType="body-small"
																noWrap
															>
																{$format('label_cancelled', {
																	default: 'Cancelled',
																})}
															</Text>
															<Icon
																size="s"
																icon="fas fa-circle-xmark text-danger"
															/>
														{/if}
													</div>
												</div>
												<div class="width-full mt-s mb-s">
													{#if paypalInfo.billingTotalDays - paypalInfo.billingTimePassed > 0}
														<ProgressBar
															class="width-full"
															progress={paypalInfo.billingProgressPercentage}
														/>
													{/if}
												</div>
												<div
													class="d-flex justify-content-between align-items-start"
												>
													<div>
														{#if paypalInfo.billingTotalDays - paypalInfo.billingTimePassed > 0}
															<Text styleType="body-small" noWrap>
																{paypalInfo.billingTimePassed}/{paypalInfo.billingTotalDays}
																{$format('text_premium_time_passed', {
																	default: 'days',
																})}
															</Text>
														{/if}
														{#if paypalInfo.subscriptionPlan === 'monthly' && paypalInfo.subscriptionStatus === 'ACTIVE' && !sharedHomeSubscriptionLocked}
															<Text
																styleType="body-small"
																class="link-styles text-primary"
																on:click={() =>
																	handleSubscriptionChecks(3)}
																noWrap
															>
																{$format('upgrade_now_text', {
																	default: 'Upgrade now',
																})}
															</Text>
														{/if}
														{#if paypalInfo.subscriptionStatus === 'CANCELLED' && !sharedHomeSubscriptionLocked}
															<Text
																styleType="controls-small"
																class="link-styles text-primary"
																on:click={() => handleRenew(1)}
																noWrap
															>
																{$format('renew_now_text', {
																	default: 'Renew now',
																})}
															</Text>
														{/if}
													</div>
													<div class="text-right flex-shrink-0">
														{#if ['ACTIVE', 'SUSPENDED'].includes(paypalInfo.subscriptionStatus)}
															<Text
																styleType="body-small"
																class="text-uppercase text-color-muted text-bold"
																noWrap
															>
																{$format('upcoming_payment_heading', {
																	default: 'Upcoming Payment',
																})}
															</Text>
														{:else if paypalInfo.subscriptionStatus === 'CANCELLED'}
															<Text
																styleType="body-small"
																class="text-uppercase text-color-muted text-bold"
																noWrap
															>
																{$format('active_untill_heading', {
																	default: 'Active until',
																})}
															</Text>
														{/if}
														<Text styleType="body-small" noWrap>
															{convertDatePayment(
																paypalInfo.nextBillingTime,
															)}
														</Text>
													</div>
												</div>
											</div>
										</Container>
									</div>
									<Text slot="customContent" styleType="body-small">
										{$format('text_popover_paused_subscription', {
											default:
												'Your subscription is currently paused and you have {leftDays} days left. It will continue after the voucher period expires.',
											values: {
												leftDays:
													paypalInfo.billingTotalDays -
													paypalInfo.billingTimePassed,
											},
										})}
									</Text>
								</Popover>
							</CardGrid>
						{/if}
						{#if $camera_cloud_storage.activePlan}
							<Text
								styleType="body-small"
								class="text-uppercase text-color-muted text-bold mb-s mt-m"
								noWrap
							>
								{$format('label_shelly_cloud_cameras_storage_add_on', {
									default: 'Shelly Cloud Cameras Storage Add On',
								})}
							</Text>
							<CardGrid sm={300} lg={350} xl={425} noPaddingBottom>
								<CameraCloudStoragePlanInfoCard
									activePlan={$camera_cloud_storage.activePlan}
									on:selectPlan={scrollToPlans}
								/>
							</CardGrid>
						{/if}
					</div>
				{:else}
					<div class="width-full">
						<div>
							<Text class="text-primary" styleType="h1" noWrap>
								{capFirstChar($format('type_push', { default: 'Standard' }))}
							</Text>
							<Text class="mt-xs text-color-muted">
								{$format('free_heading', { default: 'Free' })}
							</Text>
						</div>
						{#if $camera_cloud_storage.activePlan}
							<Text
								styleType="body-small"
								class="text-uppercase text-color-muted text-bold mb-s mt-m"
								noWrap
							>
								{$format('label_shelly_cloud_cameras_storage_add_on', {
									default: 'Shelly Cloud Cameras Storage Add On',
								})}
							</Text>
							<CardGrid sm={300} lg={350} xl={425} noPaddingBottom>
								<CameraCloudStoragePlanInfoCard
									activePlan={$camera_cloud_storage.activePlan}
									on:selectPlan={scrollToPlans}
								/>
							</CardGrid>
						{/if}
					</div>
				{/if}
			</div>
		{/await}
	</LineContainer>

	<Container class="mb-s" noPadding>
		<Text element="h1" class="mb-m">
			{$format('text_premium_header_benefits_list', {
				default: 'Premium Benefits',
			})}
		</Text>
		<PremiumBenefitsList />
	</Container>

	{#if isPremium}
		<Container class="mb-s width-full" noPadding>
			{#each settings_components as setting_components}
				<Text element="h2" class="mb-s text-capitalize">
					{$format(setting_components.title, {
						default: setting_components.default,
					})}
				</Text>

				{#if setting_components.components}
					<div class="d-flex width-full overflow-hidden">
						{#each setting_components.components as item}
							{#if item}
								<Collapse
									id="collapse-qi1upc6"
									class="width-full"
									title={$format(item.title, { default: item.default })}
									expanded={collapsesExpanded[item.id]}
									on:click={() => {
										collapsesExpanded = {
											...collapsesExpanded,
											[item.id]: !collapsesExpanded[item.id],
										};
									}}
								>
									<svelte:component this={item.component} />
								</Collapse>
							{/if}
						{/each}
					</div>
				{/if}
			{/each}</Container
		>
	{/if}

	{#key premiumInfoKey}
		{#await paypalInfoPromise}
			<div class="d-flex justify-content-center">
				<Loader />
			</div>
		{:then paypalInfo}
			<div bind:this={subscriptionsContainerRef}>
				<Container class="mb-s pb-0" noScroll>
					<Text class="mb-s" styleType="h2">
						{$format('choose_plan', { default: 'Choose Plan' })}
					</Text>

					{#if sharedHomeSubscriptionLocked}
						{#if $showNewLayout}
							<AlertV2
								class="mb-s"
								heading={$format('shared_home_subscription_managed_by_owner', {
									default: 'Managed by the home owner.',
								})}
								message={$format('shared_home_subscription_managed_by_owner_desc', {
									default:
										'Premium subscription for this home is managed by its owner. To change the plan, contact the home owner.',
								})}
							/>
						{:else}
							<Alert
								type="info"
								class="mb-s"
								heading={$format('shared_home_subscription_managed_by_owner', {
									default: 'Managed by the home owner.',
								})}
								message={$format('shared_home_subscription_managed_by_owner_desc', {
									default:
										'Premium subscription for this home is managed by its owner. To change the plan, contact the home owner.',
								})}
								isHeadingBold
							/>
						{/if}
					{/if}

					<!-- Show alert only during Black Friday -->
					{#if !$profile_data.premiumBlackFridayAlert?.seen && IS_DURING_PROMOTION && paypalInfo?.subscriptionPlan !== 'monthly'}
						<Alert
							type="info"
							class="mb-s"
							heading={$format('alert_heading_black_friday_ends', {
								default: 'Black Friday offer ends on 03-12-24.',
							})}
							dismissable
							customIcon="fa-regular fa-badge-percent"
							on:dismissable_click={updateHomeData}
						/>
					{/if}

					<div
						style="position: relative; display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); grid-gap: var(--spacing-s); z-index: 0;"
					>
						<!-- Montly subscription -->
						<PremiumCard
							class="mr-s"
							backgroundColor={$showNewLayout ? 'color-1' : undefined}
							isActive={paypalInfo.subscriptionPlan === 'monthly' &&
								paypalInfo.subscriptionStatus === 'ACTIVE'}
						>
							<Text class="mb-s" styleType="tiny">
								{$format('subscription_plan_monthly', { default: 'MONTHLY' })}
							</Text>
							{#if !paypalInfo.subscriptionStatus}
								<Label
									class="mb-s text-capitalize"
									value={$format('label_free_trial', {
										values: { freePeriod },
										default: `${freePeriod} Months Free Trial`,
									})}
									type="premium"
									size="s"
								/>
							{/if}

							<Text class="mb-s text-bold" styleType="controls-large">
								{$format('subscription_plan_monthly_price', {
									values: { price: monthlySubValue },
									default: `${monthlySubValue} Euro/Month/per House`,
								})}
							</Text>
							<Text class="mb-s" styleType="body-extra-small">
								{$format('subscription_plan_build_every_month', {
									default: 'Billed every month',
								})}
							</Text>
							<Text class="mb-s" styleType="body-extra-small">
								{$format('subscription_plan_cancel', {
									default: 'Cancel anytime',
								})}
							</Text>
							{#if paypalInfo.subscriptionPlan === 'monthly'}
								<div class="d-flex align-items-center">
									{#if paypalInfo.subscriptionStatus === 'ACTIVE'}
										<Icon class="mr-xs" icon="fas fa-circle-check text-success" />
										<Text class="text-bold text-success" styleType="controls-large">
											{$format('text_subscription_active', {
												default: 'Active',
											})}
										</Text>
									{:else if paypalInfo.subscriptionStatus === 'SUSPENDED'}
										<Icon class="mr-xs" icon="fas fa-circle-pause" />
										<Text class="text-bold" styleType="controls-large">
											{$format('label_paused', { default: 'Paused' })}
										</Text>
									{:else if paypalInfo.subscriptionStatus === 'CANCELLED'}
										<Button
											loading={isMonthlySubscribeLoading}
											disabled={sharedHomeSubscriptionLocked ||
												isMonthlySubscribeLoading}
											size="m"
											variant="premium"
											on:click={() => handleRenew(1)}
										>
											{$format('button_subscription_cancelled', {
												default: 'Renew',
											})}
										</Button>
									{:else}
										<Button size="m" variant="premium">
											{$format('button_subscription_subscribe', {
												default: 'Subscribe',
											})}
										</Button>
									{/if}
								</div>
							{:else}
								<Button
									loading={isMonthlySubscribeLoading}
									size="m"
									variant="premium"
									disabled={sharedHomeSubscriptionLocked ||
										['ACTIVE', 'SUSPENDED'].includes(
											paypalInfo.subscriptionStatus,
										) ||
										isMonthlySubscribeLoading}
									on:click={() => handleSubscriptionChecks(1)}
								>
									{$format('button_subscription_subscribe', {
										default: 'Subscribe',
									})}
								</Button>
							{/if}
						</PremiumCard>

						<!-- Yearly subscription -->
						<!-- If Basic user -->
						{#if determinePremiumCard(paypalInfo) === UserPremiumStatus.BASIC_FROM_BF_DURING}
							<PremiumCard
								class="mr-s"
								backgroundColor={$showNewLayout ? 'color-1' : 'color-warning-10'}
								isActive={paypalInfo.subscriptionPlan === 'yearly' &&
									paypalInfo.subscriptionStatus === 'ACTIVE'}
							>
								<Text class="mb-s" styleType="tiny"
									>{$format('subscription_plan_yearly', {
										default: 'YEARLY',
									})}</Text
								>
								<Label
									slot="leftBadge"
									value={$format('label_black_friday_price', {
										default: 'Black Friday Price',
									})}
									type="premium"
									icon="fas fa-badge-percent"
									size="s"
									noRadiusType="second"
								/>
								<Label
									class="mb-s"
									value={$format('label_value_free_trial_premium', {
										default: '3 Months Free Trial',
									})}
									type="premium"
									size="s"
								/>
								<div class="d-flex align-items-center">
									<Text
										styleType="body-small"
										class="text-decoration-line-through mr-xs text-color-muted"
									>
										35.99
									</Text>
									<Text class="text-bold" styleType="controls-large"
										>{$format('subscription_plan_yearly_price_bf', {
											values: { price: 24.99 },
											default: '24.99 Euro/First Year/per House',
										})}</Text
									>
								</div>
								<Text class="mb-s mt-s" styleType="body-extra-small"
									>{$format('subscription_plan_build_every_year', {
										default: 'Then 35.99 EUR/Year. Billed every 12 months',
									})}
								</Text>
								{#if paypalInfo.subscriptionPlan === 'yearly'}
									<div class="d-flex align-items-center">
										{#if paypalInfo.subscriptionStatus === 'ACTIVE'}
											<Icon
												class="mr-xs"
												icon="fas fa-circle-check text-success"
											/>
											<Text
												class="text-bold text-success"
												styleType="controls-large"
											>
												{$format('text_subscription_active', {
													default: 'Active',
												})}
											</Text>
										{:else if paypalInfo.subscriptionStatus === 'CANCELLED'}
											<Button
												loading={isYearlySubscribeLoading}
												disabled={sharedHomeSubscriptionLocked ||
													isYearlySubscribeLoading}
												size="m"
												variant="premium"
												on:click={() => handleRenew(2)}
												>{$format('button_subscription_cancelled', {
													default: 'Renew',
												})}</Button
											>
										{:else}
											<Button size="m" variant="premium"
												>{$format('button_subscription_subscribe', {
													default: 'Subscribe',
												})}</Button
											>
										{/if}
									</div>
								{:else}
									<Button
										loading={isYearlySubscribeLoading}
										disabled={sharedHomeSubscriptionLocked ||
											isYearlySubscribeLoading}
										size="m"
										variant="premium"
										on:click={() =>
											handleSubscriptionChecks(
												paypalInfo.subscriptionPlan === 'monthly' &&
													paypalInfo.subscriptionStatus === 'ACTIVE'
													? 3
													: 2,
											)}
									>
										{paypalInfo.subscriptionPlan === 'monthly' &&
										paypalInfo.subscriptionStatus === 'ACTIVE'
											? $format('button_subscription_upgrade', {
													default: 'Upgrade',
												})
											: $format('button_subscription_subscribe', {
													default: 'Subscribe',
												})}
									</Button>
								{/if}
							</PremiumCard>
						{:else if determinePremiumCard(paypalInfo) === UserPremiumStatus.BASIC_DEFAULT}
							<!-- If Premium user from BLACK FRIDAY plan and DURING promotion -->
							<PremiumCard
								class="mr-s"
								backgroundColor={$showNewLayout ? 'color-1' : undefined}
								isActive={paypalInfo.subscriptionPlan === 'yearly' &&
									paypalInfo.subscriptionStatus === 'ACTIVE'}
							>
								<Label
									slot="leftBadge"
									value={$format('label_save_25%', { default: 'Save 25%' })}
									type="premium"
									icon="fas fa-coins"
									size="s"
									noRadiusType="second"
								/>

								<Text class="mb-s" styleType="tiny"
									>{$format('subscription_plan_yearly', {
										default: 'YEARLY',
									})}</Text
								>

								{#if !paypalInfo.subscriptionStatus}
									<Label
										class="mb-s text-capitalize"
										value={$format('label_free_trial', {
											values: { freePeriod: freePeriod },
											default: `${freePeriod} Months Free Trial`,
										})}
										type="premium"
										size="s"
									/>
								{/if}

								<Text class="mb-s text-bold" styleType="controls-large">
									{$format('subscription_plan_yearly_price', {
										values: { price: yearlySubValue },
										default: `${yearlySubValue} Euro/Year/per House`,
									})}
								</Text>
								<Text class="mb-s" styleType="body-extra-small"
									>{$format('subscription_plan_build_every_12_months', {
										default: 'Billed every 12 months',
									})}</Text
								>
								<Text class="mb-s" styleType="body-extra-small"
									>{$format('subscription_plan_cancel', {
										default: 'Cancel anytime',
									})}</Text
								>
								{#if paypalInfo.subscriptionPlan === 'yearly'}
									<div class="d-flex align-items-center">
										{#if paypalInfo.subscriptionStatus === 'ACTIVE'}
											<Icon
												class="mr-xs"
												icon="fas fa-circle-check text-success"
											/>
											<Text
												class="text-bold text-success"
												styleType="controls-large"
											>
												{$format('text_subscription_active', {
													default: 'Active',
												})}
											</Text>
										{:else if paypalInfo.subscriptionStatus === 'SUSPENDED'}
											<Icon class="mr-xs" icon="fas fa-circle-pause" />
											<Text class="text-bold" styleType="controls-large">
												{$format('label_paused', { default: 'Paused' })}
											</Text>
										{:else if paypalInfo.subscriptionStatus === 'CANCELLED'}
											<Button
												loading={isYearlySubscribeLoading}
												disabled={sharedHomeSubscriptionLocked ||
													isYearlySubscribeLoading}
												size="m"
												variant="premium"
												on:click={() => handleRenew(2)}
												>{$format('button_subscription_cancelled', {
													default: 'Renew',
												})}</Button
											>
										{:else}
											<Button size="m" variant="premium"
												>{$format('button_subscription_subscribe', {
													default: 'Subscribe',
												})}</Button
											>
										{/if}
									</div>
								{:else}
									<Button
										loading={isYearlySubscribeLoading}
										disabled={sharedHomeSubscriptionLocked ||
											isYearlySubscribeLoading}
										size="m"
										variant="premium"
										on:click={() =>
											handleSubscriptionChecks(
												paypalInfo.subscriptionPlan === 'monthly' &&
													['ACTIVE', 'SUSPENDED'].includes(
														paypalInfo.subscriptionStatus,
													)
													? 3
													: 2,
											)}
									>
										{paypalInfo.subscriptionPlan === 'monthly' &&
										['ACTIVE', 'SUSPENDED'].includes(paypalInfo.subscriptionStatus)
											? $format('button_subscription_upgrade', {
													default: 'Upgrade',
												})
											: $format('button_subscription_subscribe', {
													default: 'Subscribe',
												})}
									</Button>
								{/if}
							</PremiumCard>
						{:else if determinePremiumCard(paypalInfo) === UserPremiumStatus.PREMIUM_FROM_BF_DURING}
							<PremiumCard
								class="mr-s"
								backgroundColor={$showNewLayout ? 'color-1' : 'color-warning-10'}
								isActive={paypalInfo.subscriptionPlan === 'yearly' &&
									paypalInfo.subscriptionStatus === 'ACTIVE'}
							>
								<Text class="mb-s" styleType="tiny"
									>{$format('subscription_plan_yearly', {
										default: 'YEARLY',
									})}</Text
								>
								<Label
									slot="leftBadge"
									value={$format('label_black_friday_price', {
										default: 'Black Friday Price',
									})}
									type="premium"
									icon="fas fa-badge-percent"
									size="s"
									noRadiusType="second"
								/>
								<Label
									class="mb-s"
									value={$format('label_value_free_trial_premium', {
										default: '3 Months Free Trial',
									})}
									type="premium"
									size="s"
								/>
								<div class="d-flex align-items-center">
									<Text
										styleType="body-small"
										class="text-decoration-line-through mr-xs text-color-muted"
									>
										35.99
									</Text>
									<Text class="text-bold" styleType="controls-large"
										>{$format('subscription_plan_yearly_price_bf', {
											values: { price: 24.99 },
											default: '24.99 Euro/First Year',
										})}</Text
									>
								</div>
								<Text class="mb-s mt-s" styleType="body-extra-small"
									>{$format('subscription_plan_build_every_year', {
										default: 'Then 35.99 EUR/Year. Billed every 12 months',
									})}
								</Text>
								{#if paypalInfo.subscriptionPlan === 'yearly'}
									<div class="d-flex align-items-center">
										{#if paypalInfo.subscriptionStatus === 'ACTIVE'}
											<Icon
												class="mr-xs"
												icon="fas fa-circle-check text-success"
											/>
											<Text
												class="text-bold text-success"
												styleType="controls-large"
											>
												{$format('text_subscription_active', {
													default: 'Active',
												})}
											</Text>
										{:else if paypalInfo.subscriptionStatus === 'CANCELLED'}
											<Button
												loading={isYearlySubscribeLoading}
												disabled={sharedHomeSubscriptionLocked ||
													isYearlySubscribeLoading}
												size="m"
												variant="premium"
												on:click={() => handleRenew(2)}
												>{$format('button_subscription_cancelled', {
													default: 'Renew',
												})}</Button
											>
										{:else}
											<Button size="m" variant="premium"
												>{$format('button_subscription_subscribe', {
													default: 'Subscribe',
												})}</Button
											>
										{/if}
									</div>
								{:else}
									<Button
										loading={isYearlySubscribeLoading}
										disabled={sharedHomeSubscriptionLocked ||
											isYearlySubscribeLoading}
										size="m"
										variant="premium"
										on:click={() =>
											handleSubscriptionChecks(
												paypalInfo.subscriptionPlan === 'monthly' &&
													paypalInfo.subscriptionStatus === 'ACTIVE'
													? 3
													: 2,
											)}
									>
										{paypalInfo.subscriptionPlan === 'monthly' &&
										paypalInfo.subscriptionStatus === 'ACTIVE'
											? $format('button_subscription_upgrade', {
													default: 'Upgrade',
												})
											: $format('button_subscription_subscribe', {
													default: 'Subscribe',
												})}
									</Button>
								{/if}
							</PremiumCard>
							<!-- If Premium user from BLACK FRIDAY plan and AFTER promotion -->
						{:else if determinePremiumCard(paypalInfo) === UserPremiumStatus.PREMIUM_FROM_BF_AFTER}
							<PremiumCard
								class="mr-s"
								backgroundColor={$showNewLayout ? 'color-1' : undefined}
								isActive={paypalInfo.subscriptionPlan === 'yearly' &&
									paypalInfo.subscriptionStatus === 'ACTIVE'}
							>
								<Text class="mb-s" styleType="tiny"
									>{$format('subscription_plan_yearly', {
										default: 'YEARLY',
									})}</Text
								>
								<Label
									class="mb-s"
									value={$format('label_value_free_trial_premium', {
										default: '3 Months Free Trial',
									})}
									type="premium"
									size="s"
								/>
								<div class="d-flex align-items-center">
									<Text
										styleType="body-small"
										class="text-decoration-line-through mr-xs text-color-muted"
									>
										35.99
									</Text>
									<Text class="text-bold" styleType="controls-large"
										>{$format('subscription_plan_yearly_price_bf', {
											values: { price: 24.99 },
											default: '24.99 Euro/First Year',
										})}</Text
									>
								</div>
								<Text class="mb-s mt-s" styleType="body-extra-small"
									>{$format('subscription_plan_build_every_year', {
										default: 'Then 35.99 EUR/Year. Billed every 12 months',
									})}
								</Text>
								{#if paypalInfo.subscriptionPlan === 'yearly'}
									<div class="d-flex align-items-center">
										{#if paypalInfo.subscriptionStatus === 'ACTIVE'}
											<Icon
												class="mr-xs"
												icon="fas fa-circle-check text-success"
											/>
											<Text
												class="text-bold text-success"
												styleType="controls-large"
											>
												{$format('text_subscription_active', {
													default: 'Active',
												})}
											</Text>
										{:else if paypalInfo.subscriptionStatus === 'CANCELLED'}
											<Button
												loading={isYearlySubscribeLoading}
												disabled={sharedHomeSubscriptionLocked ||
													isYearlySubscribeLoading}
												size="m"
												variant="premium"
												on:click={() => handleRenew(2)}
												>{$format('button_subscription_cancelled', {
													default: 'Renew',
												})}</Button
											>
										{:else}
											<Button size="m" variant="premium"
												>{$format('button_subscription_subscribe', {
													default: 'Subscribe',
												})}</Button
											>
										{/if}
									</div>
								{:else}
									<Button
										loading={isYearlySubscribeLoading}
										disabled={sharedHomeSubscriptionLocked ||
											isYearlySubscribeLoading}
										size="m"
										variant="premium"
										on:click={() =>
											handleSubscriptionChecks(
												paypalInfo.subscriptionPlan === 'monthly' &&
													paypalInfo.subscriptionStatus === 'ACTIVE'
													? 3
													: 2,
											)}
									>
										{paypalInfo.subscriptionPlan === 'monthly' &&
										paypalInfo.subscriptionStatus === 'ACTIVE'
											? $format('button_subscription_upgrade', {
													default: 'Upgrade',
												})
											: $format('button_subscription_subscribe', {
													default: 'Subscribe',
												})}
									</Button>
								{/if}
							</PremiumCard>
							<!-- If Normal Premium user not from Black FRIDAY plan -->
						{:else if determinePremiumCard(paypalInfo) === UserPremiumStatus.PREMIUM_DEFAULT}
							<PremiumCard
								class="mr-s"
								backgroundColor={$showNewLayout ? 'color-1' : undefined}
								isActive={paypalInfo.subscriptionPlan === 'yearly' &&
									paypalInfo.subscriptionStatus === 'ACTIVE'}
							>
								<Label
									slot="leftBadge"
									value={$format('label_save_25%', { default: 'Save 25%' })}
									type="premium"
									icon="fas fa-coins"
									size="s"
									noRadiusType="second"
								/>

								<Text class="mb-s" styleType="tiny"
									>{$format('subscription_plan_yearly', {
										default: 'YEARLY',
									})}</Text
								>

								{#if !paypalInfo.subscriptionStatus}
									<Label
										class="mb-s text-capitalize"
										value={$format('label_free_trial', {
											values: { freePeriod: freePeriod },
											default: `${freePeriod} Months Free Trial`,
										})}
										type="premium"
										size="s"
									/>
								{/if}

								<Text class="mb-s text-bold" styleType="controls-large">
									{$format('subscription_plan_yearly_price', {
										values: { price: yearlySubValue },
										default: `${yearlySubValue} Euro/Year`,
									})}
								</Text>
								<Text class="mb-s" styleType="body-extra-small"
									>{$format('subscription_plan_build_every_12_months', {
										default: 'Billed every 12 months',
									})}</Text
								>
								<Text class="mb-s" styleType="body-extra-small"
									>{$format('subscription_plan_cancel', {
										default: 'Cancel anytime',
									})}</Text
								>
								{#if paypalInfo.subscriptionPlan === 'yearly'}
									<div class="d-flex align-items-center">
										{#if paypalInfo.subscriptionStatus === 'ACTIVE'}
											<Icon
												class="mr-xs"
												icon="fas fa-circle-check text-success"
											/>
											<Text
												class="text-bold text-success"
												styleType="controls-large"
											>
												{$format('text_subscription_active', {
													default: 'Active',
												})}
											</Text>
										{:else if paypalInfo.subscriptionStatus === 'SUSPENDED'}
											<Icon class="mr-xs" icon="fas fa-circle-pause" />
											<Text class="text-bold" styleType="controls-large">
												{$format('label_paused', { default: 'Paused' })}
											</Text>
										{:else if paypalInfo.subscriptionStatus === 'CANCELLED'}
											<Button
												loading={isYearlySubscribeLoading}
												disabled={sharedHomeSubscriptionLocked ||
													isYearlySubscribeLoading}
												size="m"
												variant="premium"
												on:click={() => handleRenew(2)}
												>{$format('button_subscription_cancelled', {
													default: 'Renew',
												})}</Button
											>
										{:else}
											<Button size="m" variant="premium"
												>{$format('button_subscription_subscribe', {
													default: 'Subscribe',
												})}</Button
											>
										{/if}
									</div>
								{:else}
									<Button
										loading={isYearlySubscribeLoading}
										disabled={sharedHomeSubscriptionLocked ||
											isYearlySubscribeLoading}
										size="m"
										variant="premium"
										on:click={() =>
											handleSubscriptionChecks(
												paypalInfo.subscriptionPlan === 'monthly' &&
													['ACTIVE', 'SUSPENDED'].includes(
														paypalInfo.subscriptionStatus,
													)
													? 3
													: 2,
											)}
									>
										{paypalInfo.subscriptionPlan === 'monthly' &&
										['ACTIVE', 'SUSPENDED'].includes(paypalInfo.subscriptionStatus)
											? $format('button_subscription_upgrade', {
													default: 'Upgrade',
												})
											: $format('button_subscription_subscribe', {
													default: 'Subscribe',
												})}
									</Button>
								{/if}
							</PremiumCard>
						{/if}
					</div>

					<div
						class="d-flex justify-content-center align-items-center flex-wrap mt-s mb-md-m mb-sm-s"
					>
						{#if !sharedHomeSubscriptionLocked}
							<Text class="text-center text-color-muted" styleType="body-extra-small">
								{$format('have_voucher_heading', {
									default: 'Have a voucher for Premium Subscription?',
								})}
								<!-- Ivo: insert correct href/on:click with redirect -->

								<Text
									element="a"
									class="d-inline-flex link-underline size-m-button-min-height align-items-center"
									styleType="body-extra-small"
									on:click={() => (openVoucherModal = true)}
								>
									{$format('redeem_your_voucher_prompt', {
										default: 'Redeem your voucher',
									})}
								</Text>
							</Text>
						{/if}
					</div>
				</Container>
			</div>

			<!-- Camera Cloud Storage Add-on section (FE-1666). Visible to every signed-in user;
			     the section itself decides between the marketing state and the active-plan view. -->
			<CameraCloudStorageSection />

			{#if !sharedHomeSubscriptionLocked}
				<Container class="mb-s" noPadding>
					{#each billing_components as billing_component}
						<Text element="h2" class="mb-s text-capitalize">
							{$format(`text_title_premium_${billing_component.title}`, {
								default: billing_component.default,
							})}
						</Text>
						{#if billing_component.components}
							<Stack gap="s">
								{#each billing_component.components as item}
									{#if (item && item.id !== 'cancelSubscription') || (item && item.id === 'cancelSubscription' && (['ACTIVE', 'SUSPENDED'].includes(paypalInfo.subscriptionStatus) || $camera_cloud_storage.activePlan?.status === CameraCloudStorageSubscriptionStatus.Active))}
										<Collapse
											id="collapse-519mvib"
											title={$format(item.title, { default: item.default })}
											expanded={collapsesExpanded[item.id]}
											on:click={() => {
												collapsesExpanded = {
													...collapsesExpanded,
													[item.id]: !collapsesExpanded[item.id],
												};
											}}
										>
											<svelte:component
												this={item.component}
												{paypalInfo}
												bind:showCancelSubscriptionModal
												on:cancelled={handleCameraSubscriptionCancelled}
											/>
										</Collapse>
									{/if}
								{/each}
							</Stack>
						{/if}
					{/each}
				</Container>
			{/if}
		{/await}
	{/key}
{/key}

<Modal
	on:close={() => (showCancelSubscriptionModal = false)}
	showModal={showCancelSubscriptionModal}
	title={$format('modal_title_cancel_premium_subscription', {
		default: 'Cancel Premium Subscription?',
	})}
>
	<Container slot="content" padding="0">
		<div class="mb-m d-flex align-items-center flex-direction-column">
			<img
				class="mb-m"
				src="./images/cancel_premium_subscription.svg"
				alt="cancel-premium-subscription"
			/>
			<p class="h1-styles text-center">
				{$format('modal_subtitle_cancel_premium_subscription', {
					default: 'You’ll lose access and your configurations for:',
				})}
			</p>
		</div>
		<PremiumBenefits />
		<Alert
			type="warning"
			heading={$format('text_premium_cancel_please_note', {
				default:
					'Please note that if you cancel your subscriptions you will lose all Premium features and their settings and configurations after your billing period expires. You will need to set them up again from scratch.',
			})}
			class="mt-m mb-m"
		/>
		<Text
			>{$format('text_cancel_premium_are_you_sure', {
				default: 'Are you sure you want to cancel your premium subscription?',
			})}</Text
		>
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="width-md-l width-sm-full"
			variant="secondary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => (showCancelSubscriptionModal = false)}
		>
			{$format('button_stay_premium', {
				default: 'Stay premium',
			})}
		</Button>
		<Button
			class="width-md-l width-sm-full ml-s"
			variant="danger"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={handleCancelSubscription}
		>
			{$format('modal_information_cancel_subscription', {
				default: 'Cancel subscription',
			})}
		</Button>
	</div>
</Modal>

{#if showInvoiceDetailsModal}
	<InvoiceDetailsModal
		bind:showModal={showInvoiceDetailsModal}
		callback={() => onSubscribe(currentType)}
		initial={true}
		title={capFirstChar(
			$format('modal_title_subscription_details', {
				default: 'subscription details',
			}),
		)}
	/>
{/if}

{#if showAcceptTermsModal}
	<AcceptTermsModal bind:showModal={showAcceptTermsModal} callback={() => onSubscribe(currentType)} />
{/if}

{#if showSuccessfullySubscribedModal}
	<Modal
		on:close={onCloseWizard}
		title={capFirstChar(
			$format('modal_title_welcome_to_premium', {
				default: 'Welcome to Premium',
			}),
		)}
		hideSlotToolbar={hideWelcomeChrome}
		hideSlotFooter={hideWelcomeChrome || (isManageStorageStep && manageStorageView !== 'main')}
		showModal={showSuccessfullySubscribedModal}
		removeScrollPadding
	>
		<Wizard slot="toolbar">
			{#each wizardItems as item, i (i)}
				<WizardItem
					on:click={(e) => {
						onClickWizardItem(i);
					}}
					active={activeWizardStep.activeIndex === i}
					title={item.title}
					indicatorColor={$showNewLayout ? 'color-2' : undefined}
				/>
			{/each}
		</Wizard>

		<div slot="content" class="height-full">
			{#if showConfetti}
				<div
					class="d-flex justify-content-center overflow-hidden"
					style="position: fixed; top: 40vh; left: 50vw; height: 100vh; z-index: 1;"
				>
					<Confetti
						delay={[0, 200]}
						amount={isMobile ? 100 : 200}
						y={[-2, 2]}
						x={isMobile ? [-1.5, 1.5] : [-5, 5]}
						fallDistance={'100px'}
					/>
				</div>
			{/if}
			<WizardContainer
				class="max-height-100"
				activeStepIndex={activeWizardStep.activeIndex}
				adaptiveHeight
			>
				{#each wizardItems as item, i (i)}
					<WizardContent
						active={activeWizardStep.activeIndex === i}
						class={classMap({
							'max-height-100': setHight100Class && isWeatherStep,
						})}
					>
						{#if i === 0}
							<SuccessfullySubscribed
								showDismissButton={hideWelcomeChrome}
								on:click={onCloseWizard}
							/>
						{:else if includeCameraStorageStep && i === 1}
							<ManageCameraCloudStoragePanel
								bind:this={manageStoragePanel}
								bind:view={manageStorageView}
								activePlan={devPreviewPlan ?? $camera_cloud_storage.activePlan}
								slotCountOverride={devPreviewSlotCount}
								previewMode={devWizardPreview}
								showPickerActions
								welcomeWizard
								on:upgrade={() => {
									onCloseWizard();
									void scrollToPlans();
								}}
							/>
						{:else}
							<SetUpWeatherLocation
								bind:weatherStationSaveData
								mapParentSize
								resizeMap={setHight100Class && isWeatherStep}
								on:open={() => (setHight100Class = false)}
							/>
						{/if}
					</WizardContent>
				{/each}
			</WizardContainer>
		</div>
		<div slot="footer" class="d-flex justify-content-end gap-s">
			{#if isManageStorageStep}
				<svelte:component
					this={WelcomeWizardButton}
					class={welcomeWizardButtonClass}
					responsiveSize={{
						sm: 'm',
					}}
					size="m"
					variant="secondary"
					on:click={goToNextWelcomeStep}
				>
					{$format('button_camera_cloud_storage_skip_assignment', { default: 'Skip for now' })}
				</svelte:component>
				<svelte:component
					this={WelcomeWizardButton}
					class={welcomeWizardButtonClass}
					icon="fas fa-save"
					responsiveSize={{
						sm: 'm',
					}}
					size="m"
					on:click={saveCameraSlotsAndContinue}
				>
					{isLastStepWizard
						? $format('button_save', { default: 'Save' })
						: $format('button_save_and_continue', { default: 'Save and continue' })}
				</svelte:component>
			{:else}
				<svelte:component
					this={WelcomeWizardButton}
					class={welcomeWizardButtonClass}
					icon="fas fa-chevron-left"
					responsiveSize={{
						sm: 'm',
					}}
					size="m"
					disabled={isFirstStep}
					variant="secondary"
					on:click={() => {
						onClickPrevNextButton('prev');
					}}
				>
					{$format('button_back', { default: 'Back' })}
				</svelte:component>
				<svelte:component
					this={WelcomeWizardButton}
					class={welcomeWizardButtonClass}
					icon={isLastStepWizard ? 'fas fa-save' : ''}
					disabled={(isWeatherStep && !weatherStationSaveData) || waitForSaveResponse}
					rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
					responsiveSize={{
						sm: 'm',
					}}
					size="m"
					on:click={async () => {
						if (isLastStepWizard) {
							await saveStation();
						} else {
							onClickPrevNextButton('next');
						}
					}}
				>
					{nextButton}
				</svelte:component>
			{/if}
		</div>
	</Modal>
{/if}

{#if showCanceledSubscriptionModal}
	<Modal
		showModal={showCanceledSubscriptionModal}
		title={capFirstChar(
			$format('modal_title_leave_premium', {
				default: 'Deactivated Premium',
			}),
		)}
		on:close={() => (showCanceledSubscriptionModal = false)}
	>
		<Container slot="content" padding="0">
			<div class="d-flex justify-content-center mb-m">
				<img src="./images/deactivated-premium.svg" alt="Crossroad" />
			</div>

			<Text element="h1" class="text-center mb-s">
				{$format('toast_premium_cancel_subscription', {
					default: 'Sorry to see you go',
				})}
			</Text>
			<Text styleType="body" class="text-center text-color-muted mb-m">
				{$format('text_desc_premium_cancel_subscription', {
					default:
						'You have successfully deactivated your premium account and have gone back to basic account.',
				})}
			</Text>

			<!-- <div class="d-flex justify-content-center">
			<Text class="text-center text-primary" on:click={}>
				{$format('change_your_mind_label', {
					default: 'Change your mind? Renew Premium Membership.'
				})}
			</Text>
		</div> -->
		</Container>
	</Modal>
{/if}

{#if showCanceledCameraSubscriptionModal}
	<Modal
		showModal={showCanceledCameraSubscriptionModal}
		title={capFirstChar(
			$format('modal_title_leave_camera_cloud_storage', {
				default: 'Deactivated Camera Subscription',
			}),
		)}
		on:close={() => (showCanceledCameraSubscriptionModal = false)}
	>
		<Container slot="content" padding="0">
			<div class="d-flex justify-content-center mb-m">
				<img src="./images/deactivated-premium.svg" alt="Crossroad" />
			</div>

			<Text element="h1" class="text-center mb-s">
				{$format('text_title_camera_cloud_storage_cancel_subscription', {
					default: 'Sorry to see you go!',
				})}
			</Text>
			<Text styleType="body" class="text-center text-color-muted mb-m">
				{$format('text_desc_camera_cloud_storage_cancel_subscription', {
					default: 'You have successfully deactivated your camera cloud storage subscription.',
				})}
			</Text>

			<div class="d-flex justify-content-center">
				<Text
					class="text-center text-primary cursor-pointer"
					on:click={handleRenewCameraCloudStorage}
				>
					{$format('change_your_mind_camera_cloud_storage_label', {
						default: 'Changed your mind? Renew Camera Cloud Storage Subscription',
					})}
				</Text>
			</div>
		</Container>
	</Modal>
{/if}

<style>
	:global(.max-height-100) {
		max-height: 100%;
	}

	.lower-opacity {
		opacity: 0.5;
	}

	.line-through {
		text-decoration: line-through;
	}
</style>
